import type {
  CompanyRbacOverview,
  CompanyRoleView,
  PermissionDefinitionView,
  RoleAssignmentView,
} from '@safetyhub/shared';
import { api, ApiResponse } from './api';

export type {
  CompanyRbacOverview,
  CompanyRoleView,
  LegalPositionGuidance,
  PermissionDefinitionView,
  RoleAssignmentView,
} from '@safetyhub/shared';

/**
 * 조직 RBAC API 클라이언트 — 「역할·선임」 화면(`/settings/roles`)·초대 모달의 역할 목록이 쓴다.
 * (줄기 company-rbac P2-a, 계약 정본은 shared 타입 JSDoc + 결정 `2026-09-20-company-rbac-with-legal-positions`)
 *
 * base `/organizations/:organizationId/rbac`. 에러 코드는 응답 봉투 `error.code`(`extractApiError`)로 읽고
 * 사용자 문구 매핑은 화면(`pages/settings/roles/rbacErrors.ts`)이 소유한다.
 */
const base = (organizationId: string) => `/organizations/${organizationId}/rbac`;

export interface CreateCompanyRoleDto {
  name: string;
  description?: string;
  permissionKeys: string[];
}

/** SYSTEM 역할은 `permissionKeys`만 받는다(오버라이드 행 생성) — 이름·설명은 조직이 못 바꾼다. */
export interface UpdateCompanyRoleDto {
  name?: string;
  description?: string;
  permissionKeys?: string[];
}

export interface CreateRoleAssignmentDto {
  memberId: string;
  roleKey: string;
  /** ISO(UTC). 생략 시 서버가 지금으로. */
  assignedAt?: string;
  delegatedToAgency?: boolean;
  agencyName?: string;
  note?: string;
  /** 의사 여부(직무 신규교육 「보건관리자가 의사인 경우 1년」 · 2026-11-04) — null·생략 = 아직 모름 */
  isPhysician?: boolean | null;
}

export type UpdateRoleAssignmentDto = Partial<Omit<CreateRoleAssignmentDto, 'memberId' | 'roleKey'>>;

export const rbacApi = {
  getPermissions: async (organizationId: string): Promise<PermissionDefinitionView[]> => {
    const response = await api.get<ApiResponse<PermissionDefinitionView[]>>(`${base(organizationId)}/permissions`);
    return response.data.data!;
  },

  getRoles: async (organizationId: string): Promise<CompanyRoleView[]> => {
    const response = await api.get<ApiResponse<CompanyRoleView[]>>(`${base(organizationId)}/roles`);
    return response.data.data!;
  },

  createRole: async (organizationId: string, data: CreateCompanyRoleDto): Promise<CompanyRoleView> => {
    const response = await api.post<ApiResponse<CompanyRoleView>>(`${base(organizationId)}/roles`, data);
    return response.data.data!;
  },

  updateRole: async (
    organizationId: string,
    roleKey: string,
    data: UpdateCompanyRoleDto,
  ): Promise<CompanyRoleView> => {
    const response = await api.patch<ApiResponse<CompanyRoleView>>(
      `${base(organizationId)}/roles/${encodeURIComponent(roleKey)}`,
      data,
    );
    return response.data.data!;
  },

  /** SYSTEM 역할의 조직 오버라이드 행 삭제 → 시스템 기본값으로. */
  resetRoleOverride: async (organizationId: string, roleKey: string): Promise<void> => {
    await api.delete(`${base(organizationId)}/roles/${encodeURIComponent(roleKey)}/override`);
  },

  /** COMPANY 역할 삭제 — 보유자가 있으면 409 `ROLE_HAS_HOLDERS`. */
  deleteRole: async (organizationId: string, roleKey: string): Promise<void> => {
    await api.delete(`${base(organizationId)}/roles/${encodeURIComponent(roleKey)}`);
  },

  getAssignments: async (organizationId: string): Promise<RoleAssignmentView[]> => {
    const response = await api.get<ApiResponse<RoleAssignmentView[]>>(`${base(organizationId)}/assignments`);
    return response.data.data!;
  },

  createAssignment: async (
    organizationId: string,
    data: CreateRoleAssignmentDto,
  ): Promise<RoleAssignmentView> => {
    const response = await api.post<ApiResponse<RoleAssignmentView>>(`${base(organizationId)}/assignments`, data);
    return response.data.data!;
  },

  updateAssignment: async (
    organizationId: string,
    assignmentId: string,
    data: UpdateRoleAssignmentDto,
  ): Promise<RoleAssignmentView> => {
    const response = await api.patch<ApiResponse<RoleAssignmentView>>(
      `${base(organizationId)}/assignments/${assignmentId}`,
      data,
    );
    return response.data.data!;
  },

  deleteAssignment: async (organizationId: string, assignmentId: string): Promise<void> => {
    await api.delete(`${base(organizationId)}/assignments/${assignmentId}`);
  },

  getOverview: async (organizationId: string): Promise<CompanyRbacOverview> => {
    const response = await api.get<ApiResponse<CompanyRbacOverview>>(`${base(organizationId)}/overview`);
    return response.data.data!;
  },

  /** 상시근로자 수 저장 — 응답의 `guidance`가 정본(클라 재계산 없음). */
  updateOverview: async (
    organizationId: string,
    data: { regularWorkerCount: number | null },
  ): Promise<CompanyRbacOverview> => {
    const response = await api.patch<ApiResponse<CompanyRbacOverview>>(`${base(organizationId)}/overview`, data);
    return response.data.data!;
  },
};
